import React, { useEffect, useState, useRef } from 'react';
import { hrAPI } from '@/lib/api';
import { useAuth } from '@/contexts/AuthContext';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Table, TableHeader, TableBody, TableRow, TableHead, TableCell } from '@/components/ui/table';
import { toast } from 'sonner';
import { Plus, Pencil, Trash2, GraduationCap, Upload, FileText } from 'lucide-react';
import { GroupedSkillMultiSelect } from './GroupedSkillMultiSelect';

const BACKEND = process.env.REACT_APP_BACKEND_URL;

export default function TrainingsPage() {
  const { user } = useAuth();
  const isAdmin = user?.role === 'admin';
  const canManage = isAdmin || user?.app_roles?.hr_training === 'manager';
  const [rows, setRows] = useState([]);
  const [skills, setSkills] = useState([]);
  const [emps, setEmps] = useState([]);
  const [dlg, setDlg] = useState(null);
  const [saving, setSaving] = useState(false);
  const matRef = useRef();

  const blank = { title: '', description: '', training_type: 'skill', skills_covered: [], eligible_trainer_ids: [], external_trainer: '', question_bank: [] };

  const load = async () => {
    try {
      const [t, s, e] = await Promise.all([hrAPI.listTrainings(), hrAPI.listSkills(), hrAPI.listEmployees()]);
      setRows(t.data || []); setSkills(s.data || []); setEmps(e.data || []);
    } catch { toast.error('Failed to load'); }
  };
  useEffect(() => { load(); }, []);

  const toggle = (field, val) => setDlg(d => ({ ...d, [field]: d[field].includes(val) ? d[field].filter(x => x !== val) : [...d[field], val] }));

  const save = async () => {
    if (!dlg.title.trim()) { toast.error('Title required'); return; }
    setSaving(true);
    try {
      const payload = { ...dlg };
      let res;
      if (dlg.id) res = await hrAPI.updateTraining(dlg.id, payload);
      else res = await hrAPI.createTraining(payload);
      toast.success('Training saved');
      setDlg(res.data); load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
    finally { setSaving(false); }
  };

  const remove = async (r) => {
    if (!window.confirm(`Delete "${r.title}"?`)) return;
    try { await hrAPI.deleteTraining(r.id); toast.success('Deleted'); load(); }
    catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
  };

  const uploadMat = async (e) => {
    const file = e.target.files?.[0]; if (!file || !dlg?.id) return;
    try { const fd = new FormData(); fd.append('file', file); const r = await hrAPI.uploadMaterial(dlg.id, fd); setDlg(d => ({ ...d, materials: [...(d.materials || []), r.data] })); toast.success('Material uploaded'); }
    catch { toast.error('Upload failed'); }
    finally { if (matRef.current) matRef.current.value = ''; }
  };

  return (
    <div className="space-y-5" data-testid="hr-trainings-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold tracking-tight text-[#0F172A] flex items-center gap-2"><GraduationCap className="w-6 h-6 text-pink-600" /> Trainings</h1>
          <p className="text-sm text-slate-500 mt-0.5">Training master: skills covered, eligible trainers and material. Tests are linked from Competency Tests.</p>
        </div>
        {canManage && <Button size="sm" className="bg-pink-600 hover:bg-pink-700 text-white gap-1" onClick={() => setDlg({ ...blank })} data-testid="hr-training-add"><Plus className="w-4 h-4" /> Add Training</Button>}
      </div>

      <Card className="border border-slate-200 shadow-none"><CardContent className="p-0">
        <Table>
          <TableHeader><TableRow className="bg-slate-50/80">
            <TableHead className="text-xs">Title</TableHead><TableHead className="text-xs">Type</TableHead>
            <TableHead className="text-xs">Skills</TableHead>
            {canManage && <TableHead className="text-xs text-right w-24">Actions</TableHead>}
          </TableRow></TableHeader>
          <TableBody>
            {rows.length === 0 ? <TableRow><TableCell colSpan={4} className="text-center text-slate-400 py-8 text-sm">No trainings yet</TableCell></TableRow> :
              rows.map(r => (
                <TableRow key={r.id} data-testid={`hr-training-row-${r.id}`}>
                  <TableCell className="text-sm font-medium">{r.title}</TableCell>
                  <TableCell><Badge variant="outline" className={`text-[10px] ${r.training_type === 'skill' ? 'bg-indigo-50 text-indigo-700 border-indigo-200' : 'bg-slate-50 text-slate-600'}`}>{r.training_type === 'skill' ? 'Skill' : 'Impromptu'}</Badge></TableCell>
                  <TableCell className="text-xs text-slate-500">{(r.skills_covered_named || []).map(s => s.name).join(', ') || '—'}</TableCell>
                  {canManage && <TableCell className="text-right">
                    <Button size="icon" variant="ghost" className="h-7 w-7" onClick={() => setDlg({ ...r })} data-testid={`hr-training-edit-${r.id}`}><Pencil className="w-3.5 h-3.5" /></Button>
                    <Button size="icon" variant="ghost" className="h-7 w-7 text-red-500" onClick={() => remove(r)}><Trash2 className="w-3.5 h-3.5" /></Button>
                  </TableCell>}
                </TableRow>
              ))}
          </TableBody>
        </Table>
      </CardContent></Card>

      <Dialog open={!!dlg} onOpenChange={(o) => !o && setDlg(null)}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="hr-training-dialog">
          <DialogHeader><DialogTitle>{dlg?.id ? 'Edit Training' : 'Add Training'}</DialogTitle></DialogHeader>
          {dlg && (
            <div className="space-y-3">
              <div><Label className="text-xs">Title *</Label><Input value={dlg.title} onChange={e => setDlg({ ...dlg, title: e.target.value })} data-testid="hr-training-title" /></div>
              <div><Label className="text-xs">Description</Label><Textarea rows={2} value={dlg.description} onChange={e => setDlg({ ...dlg, description: e.target.value })} /></div>
              <div>
                <Label className="text-xs">Type</Label>
                <Select value={dlg.training_type} onValueChange={v => setDlg({ ...dlg, training_type: v })}>
                  <SelectTrigger data-testid="hr-training-type"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="skill">Skill (impacts skill matrix)</SelectItem>
                    <SelectItem value="impromptu">Impromptu (SOP/NC/rejection — no matrix update)</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs">Skills Covered</Label>
                <div className="mt-1">
                  <GroupedSkillMultiSelect skills={skills} selectedIds={dlg.skills_covered} onChange={(ids) => setDlg(d => ({ ...d, skills_covered: ids }))} testidPrefix="hr-training-skill" />
                </div>
              </div>
              <div>
                <Label className="text-xs">Eligible Trainers (S3/S4 in covered skills, or override)</Label>
                <div className="flex flex-wrap gap-1.5 mt-1 max-h-24 overflow-y-auto">
                  {emps.map(e => { const a = dlg.eligible_trainer_ids.includes(e.id); return <button key={e.id} onClick={() => toggle('eligible_trainer_ids', e.id)} className={`text-[11px] px-2 py-1 rounded border ${a ? 'bg-pink-50 text-pink-700 border-pink-300' : 'bg-white text-slate-500 border-slate-200'}`}>{e.name}</button>; })}
                </div>
              </div>
              {isAdmin && <div><Label className="text-xs">External Trainer (admin only)</Label><Input value={dlg.external_trainer || ''} onChange={e => setDlg({ ...dlg, external_trainer: e.target.value })} placeholder="External trainer name" data-testid="hr-training-external" /></div>}

              {dlg.id && (
                <div>
                  <Label className="text-xs">Training Material</Label>
                  <div className="flex items-center gap-2 mt-1">
                    <input type="file" ref={matRef} onChange={uploadMat} className="hidden" data-testid="hr-training-material-input" />
                    <Button size="sm" variant="outline" onClick={() => matRef.current?.click()} className="gap-1"><Upload className="w-3.5 h-3.5" /> Upload</Button>
                  </div>
                  <div className="mt-1 space-y-1">{(dlg.materials || []).map(m => <a key={m.id} href={`${BACKEND}/api/hr/trainings/${dlg.id}/material/${m.id}`} target="_blank" rel="noreferrer" className="flex items-center gap-1 text-xs text-blue-600 hover:underline"><FileText className="w-3 h-3" />{m.filename}</a>)}</div>
                </div>
              )}

              <div className="rounded-md bg-slate-50 border border-slate-200 p-3 text-xs text-slate-500">
                Tests are now managed separately. Create a competency test under <b>Competency Tests</b> and link it to this training.
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setDlg(null)}>Close</Button>
            <Button onClick={save} disabled={saving} className="bg-pink-600 hover:bg-pink-700 text-white" data-testid="hr-training-save">{saving ? 'Saving...' : 'Save'}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
